import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { cleanText } from '@/utils/sanitize';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Sparkles,
  Loader2,
  Wand2,
  Lightbulb,
  Route,
  Clock,
  CheckCircle,
  XCircle,
  ImageIcon,
  BookOpen,
  Undo2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNING_PATH_CREATION_PERMISSION } from '@/constants/constant-permissions';
import { learningPathApiService, type LearningPathOrchestrationDto, type CreateLearningPathDto } from '@/services/api/learning-path-api.service';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select, SelectOption } from '@/components/ui/Select';
import Pagination from '@/components/ui/Pagination';
import { languageOptions } from '@/constants/languageOptions';

interface LearningPathFormData {
  user_input: string;
  target_role_or_skill: string;
  difficulty_level: string;
  targeted_audience: string;
  industry: string;
  content_language: string;
  lp_language: string;
}


const difficultyOptions: SelectOption[] = [
  { label: 'Beginner', value: 'beginner' },
  { label: 'Intermediate', value: 'intermediate' },
  { label: 'Advanced', value: 'advanced' },
  { label: 'Mixed (Beginner to Advanced)', value: 'mixed' },
];

// Course Language options — supports "All Languages" for cross-language course matching.
const languagePreferenceOptions: SelectOption[] = [
  { label: '🌐 All Languages (Recommended)', value: 'all' },
  ...languageOptions,
];

// LP Language options — controls the LP wrapper text (title, slug, description,
// outcomes, tags, suggestions). A single LP must commit to one language, so
// "all" is NOT offered here.
const lpLanguageOptions: SelectOption[] = [...languageOptions];

export default function CreateLearningPath() {
  usePermissionRouteAccess([
    LEARNING_PATH_CREATION_PERMISSION.ADMIN_LEARNING_PATH_CREATION_READ,
    LEARNING_PATH_CREATION_PERMISSION.ADMIN_LEARNING_PATH_CREATION_WRITE,
  ]);
  const canWrite = useHasPermission([LEARNING_PATH_CREATION_PERMISSION.ADMIN_LEARNING_PATH_CREATION_WRITE]);

  const navigate = useNavigate();
  const location = useLocation();
  const ticketDescription = location.state?.ticketDescription;
  const ticketLanguageCode = location.state?.languageCode;
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [originalTextBeforeEnhancement, setOriginalTextBeforeEnhancement] = useState<string>('');
  const [recentPage, setRecentPage] = useState(1);
  const [recentItemsPerPage, setRecentItemsPerPage] = useState(6);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LearningPathFormData>({
    mode: 'all',
    defaultValues: {
      user_input: '',
      target_role_or_skill: '',
      difficulty_level: 'mixed',
      targeted_audience: '',
      industry: '',
      content_language: 'all', // Default to "All Languages" - includes courses in all languages
      lp_language: 'en-US', // LP wrapper defaults to English; admin can override
    },
  });

  const userInput = watch('user_input');
  const targetRole = watch('target_role_or_skill');

  // Pre-fill from ticket (from self-ticket request)
  useEffect(() => {
    if (ticketDescription) {
      setValue('user_input', ticketDescription, { shouldValidate: true });
    }
    if (ticketLanguageCode) {
      setValue('content_language', ticketLanguageCode);
    }
  }, [ticketDescription, ticketLanguageCode, setValue]);

  const createMutation = useMutation({
    mutationFn: (data: CreateLearningPathDto) => learningPathApiService.createLearningPath(data),
    onSuccess: (response) => {
      toast.success('Learning path generation started!');
      navigate(`/admin/learning-paths/generate/${response._id}`);
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create learning path'
      );
    },
  });

  // Fetch recent orchestrations
  const { data: recentData, isLoading: isLoadingRecent } = useQuery({
    queryKey: ['recent-learning-path-orchestrations', recentPage, recentItemsPerPage],
    queryFn: () =>
      learningPathApiService.getRecentOrchestrations({
        limit: recentItemsPerPage,
        skip: (recentPage - 1) * recentItemsPerPage,
      }),
  });

  const handleEnhanceInput = async () => {
    const currentText = watch('user_input');

    if (!currentText || currentText.trim().length < 10) {
      toast.error('Please enter at least 10 characters to enhance');
      return;
    }

    setIsEnhancing(true);
    const loadingToast = toast.loading('Enhancing your learning goal with AI...');

    try {
      const result = await learningPathApiService.enhanceUserInput({
        user_input: cleanText(currentText),
        target_role_or_skill: cleanText(targetRole) || undefined,
      });

      setOriginalTextBeforeEnhancement(currentText);
      setValue('user_input', result.enhanced_input, { shouldValidate: true });
      toast.dismiss(loadingToast);
      toast.success('Learning goal enhanced successfully!');
    } catch (error: any) {
      toast.dismiss(loadingToast);
      toast.error(
        error?.response?.data?.error?.error_description ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to enhance input'
      );
      console.error(error);
    } finally {
      setIsEnhancing(false);
    }
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // Ensure time differences are never negative - handle timing issues gracefully
    const diffMins = Math.max(0, Math.floor(diffMs / 60000));
    const diffHours = Math.max(0, Math.floor(diffMs / 3600000));
    const diffDays = Math.max(0, Math.floor(diffMs / 86400000));

    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getOverallStatus = (orch: LearningPathOrchestrationDto) => {
    const stages = [
      orch.metadata_generation_status,
      orch.image_generation_status,
      orch.course_selection_status,
    ];
    if (stages.some(s => s.status === 'failed')) return 'failed';
    if (stages.every(s => s.status === 'completed')) return 'completed';
    if (stages.some(s => s.status === 'processing')) return 'processing';
    return 'pending';
  };

  const getStatusColor = (orch: LearningPathOrchestrationDto) => {
    const status = getOverallStatus(orch);
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'processing':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusIcon = (orch: LearningPathOrchestrationDto) => {
    const status = getOverallStatus(orch);
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-3.5 h-3.5" />;
      case 'processing':
        return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
      case 'failed':
        return <XCircle className="w-3.5 h-3.5" />;
      default:
        return <Clock className="w-3.5 h-3.5" />;
    }
  };

  const getStatusText = (orch: LearningPathOrchestrationDto) => {
    const status = getOverallStatus(orch);
    if (status === 'processing') return 'Generating';
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    return 'Pending';
  };

  const handleRecentItemsPerPageChange = (newItemsPerPage: number) => {
    setRecentItemsPerPage(newItemsPerPage);
    setRecentPage(1);
  };

  const onSubmit = (data: LearningPathFormData) => {
    // Sanitize free-text before it reaches the AI generator / DB (VAPT #12/#13).
    createMutation.mutate({
      ...data,
      user_input: cleanText(data.user_input),
      target_role_or_skill: cleanText(data.target_role_or_skill),
      targeted_audience: cleanText(data.targeted_audience),
      industry: cleanText(data.industry),
    });
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
            <Route className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create AI-Powered Learning Path</h1>
            <p className="text-gray-600">Simply describe your learning goal - AI generates everything automatically</p>
          </div>
        </div>
      </div>

      {/* AI Info Alert */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-purple-900 mb-1">No Course Selection Required!</h3>
            <p className="text-sm text-purple-800 mb-2">
              Just describe what you want to learn, and AI will automatically generate:
            </p>
            <ul className="text-sm text-purple-800 space-y-1 ml-4">
              <li>• Professional learning path title and description</li>
              <li>• AI-curated course sequence from your published courses</li>
              <li>• 🌐 Courses in all languages (English, Hindi, Gujarati, etc.)</li>
              <li>• Banner and thumbnail images</li>
              <li>• Learning outcomes and categorization tags</li>
              <li>• Alternative learning path suggestions</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* AI Generation Input */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">AI Generation Input</h2>
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Describe Your Learning Goal <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {/* Undo button */}
                  {originalTextBeforeEnhancement && (
                    <button
                      type="button"
                      onClick={() => {
                        setValue('user_input', originalTextBeforeEnhancement, { shouldValidate: true });
                        setOriginalTextBeforeEnhancement('');
                        toast.success('Reverted to original text');
                      }}
                      className="p-2 rounded-lg transition-all bg-gray-100 hover:bg-gray-200 text-gray-600"
                      title="Undo enhancement"
                    >
                      <Undo2 className="w-4 h-4" />
                    </button>
                  )}

                  {/* Enhance button */}
                  <button
                    type="button"
                    onClick={handleEnhanceInput}
                    disabled={!canWrite || isEnhancing || !userInput || userInput.trim().length < 10}
                    className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm font-medium"
                    title={!canWrite ? 'You do not have permission to create learning paths' : undefined}
                  >
                    {isEnhancing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enhancing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Enhance with AI
                      </>
                    )}
                  </button>
                </div>
              </div>
              <Textarea
                name="user_input"
                control={control}
                rules={{
                  required: 'Learning goal is required',
                  minLength: { value: 20, message: 'Please provide at least 20 characters' },
                }}
                rows={4}
                placeholder="Example: I want to become a full-stack web developer proficient in modern JavaScript frameworks and able to build scalable web applications from scratch"
                error={errors.user_input?.message}
              />
            </div>

            <Input
              name="target_role_or_skill"
              control={control}
              label="Target Role or Skill"
              required
              rules={{
                required: 'Target role or skill is required',
              }}
              placeholder="e.g., Full Stack Developer, Data Scientist, UI/UX Designer"
              error={errors.target_role_or_skill?.message}
            />
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                name="difficulty_level"
                control={control}
                label="Difficulty Level"
                options={difficultyOptions}
                isSearchable={false}
              />

              <Input
                name="targeted_audience"
                control={control}
                label="Target Audience"
                placeholder="e.g., Working Professionals, Students"
                error={errors.targeted_audience?.message}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                name="industry"
                control={control}
                label="Industry"
                placeholder="e.g., Technology, Healthcare, Finance"
                error={errors.industry?.message}
              />

              <div>
                <Select
                  name="content_language"
                  control={control}
                  label="Course Language"
                  options={languagePreferenceOptions}
                  isSearchable={true}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  💡 <strong>"All Languages":</strong> Includes courses in all languages (mixed path).
                  <br />
                  <strong>Specific language:</strong> Shows ONLY courses in that language (e.g., only Hindi courses).
                </p>
              </div>

              <div>
                <Select
                  name="lp_language"
                  control={control}
                  label="Learning Path Language"
                  options={lpLanguageOptions}
                  isSearchable={true}
                  rules={{ required: 'Please select a language for the learning path' }}
                  error={errors.lp_language?.message}
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  💡 The LP title, slug, description, outcomes, tags, and suggestions will all be generated in this language.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!canWrite || isSubmitting}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium inline-flex items-center gap-2"
            title={!canWrite ? 'You do not have permission to create learning paths' : undefined}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Learning Path
              </>
            )}
          </button>
        </div>
      </form>

      {/* Recent Learning Paths */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Recent Learning Paths</h2>
        {isLoadingRecent ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto" />
          </div>
        ) : recentData && recentData.orchestrations.length > 0 ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentData.orchestrations.map(orch => (
                <div
                  key={orch._id}
                  onClick={() => navigate(`/admin/learning-paths/generate/${orch._id}`)}
                  className="border border-gray-200 rounded-lg transition-all duration-200 bg-white overflow-hidden flex flex-col h-full hover:shadow-lg cursor-pointer"
                >
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 flex-1">
                        {orch.path_title || orch.target_role_or_skill || 'Untitled Learning Path'}
                      </h3>
                    </div>

                    {/* Status and Time Row */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(orch.createdAt)}
                      </p>
                      <div className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border ${getStatusColor(orch)}`}>
                        {getStatusIcon(orch)}
                        <span className="capitalize">{getStatusText(orch)}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="flex-1">
                      {orch.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                          {orch.description}
                        </p>
                      )}
                    </div>

                    {/* Courses Stats */}
                    {!orch.no_courses_found && orch.selected_courses && (
                      <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <BookOpen className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{orch.selected_courses.length}</span>
                          <span>courses</span>
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-100 pt-4 space-y-3">
                      <div className="flex items-center justify-between text-xs font-medium text-gray-600">
                        <span>Generation Progress</span>
                        <span>
                          {[
                            orch.metadata_generation_status.status,
                            orch.image_generation_status.status,
                            orch.course_selection_status.status,
                          ].filter(status => status.toLowerCase() === 'completed').length}
                          /3
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { status: orch.metadata_generation_status.status, label: 'Metadata', icon: Lightbulb },
                          { status: orch.image_generation_status.status, label: 'Images', icon: ImageIcon },
                          { status: orch.course_selection_status.status, label: 'Courses', icon: BookOpen },
                        ].map((item, index) => {
                          const statusLower = item.status?.toLowerCase();
                          const bgColor =
                            statusLower === 'completed'
                              ? 'bg-green-500'
                              : statusLower === 'processing'
                              ? 'bg-blue-500 animate-pulse'
                              : statusLower === 'failed'
                              ? 'bg-red-500'
                              : 'bg-gray-200';

                          const iconColor =
                            statusLower === 'completed'
                              ? 'text-green-600'
                              : statusLower === 'processing'
                              ? 'text-blue-600'
                              : statusLower === 'failed'
                              ? 'text-red-600'
                              : 'text-gray-400';

                          const IconComponent = item.icon;

                          return (
                            <div key={index} className="text-center">
                              <div className="flex flex-col items-center gap-1.5">
                                <IconComponent className={`w-5 h-5 ${iconColor} transition-colors duration-300`} />
                                <div className={`w-full h-2 rounded-full transition-all duration-300 ${bgColor}`} />
                                <span className="text-[10px] text-gray-500">{item.label}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {recentData && recentData.total > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Pagination
                  currentPage={recentPage}
                  totalPages={recentData.pagination.totalPages}
                  totalItems={recentData.total}
                  itemsPerPage={recentItemsPerPage}
                  onPageChange={setRecentPage}
                  onItemsPerPageChange={handleRecentItemsPerPageChange}
                  showItemsPerPage={true}
                  itemsPerPageOptions={[6, 12, 18, 24]}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <Route className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No recent learning paths</h3>
            <p className="text-gray-600">Your recently created learning paths will appear here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
